import { TAffiliation } from '@timeedit/types/lib/types';

/**
 * The subset of TAffiliation fields that can be validated on the frontend
 * before calling the API. These correspond to the columns in
 * PartialAttributesMatchingTable that can receive a targeted error border.
 *
 * Fields not included here (standardOrganization, userObjects) can only fail
 * due to server-side checks (e.g. referenced ID not found), which fall back to
 * a row-level error border instead.
 */
export type AffiliationFieldKey = 'matchingCriteria' | 'organizationsAndRoles';

/**
 * Returns the set of TAffiliation field keys that fail frontend validation.
 * An empty set means all detectable fields are valid.
 *
 * Rules mirror those enforced by AffiliationDrawer:
 *   - Every matchingCriteria entry must have a non-empty attribute AND value.
 *   - Every organizationsAndRoles entry must have a non-empty organizationId AND role.
 */
export const validateAffiliationFields = (
  aff: Omit<TAffiliation, 'authConfigId'>
): Set<AffiliationFieldKey> => {
  const errors = new Set<AffiliationFieldKey>();

  if (aff.matchingCriteria.some((c) => !c.attribute || !c.value)) {
    errors.add('matchingCriteria');
  }

  if (aff.organizationsAndRoles.some((o) => !o.organizationId || !o.role)) {
    errors.add('organizationsAndRoles');
  }

  return errors;
};

/**
 * Produces a human-readable error message describing which specific sub-fields
 * are missing, for use in the migration result modal.
 *
 * Checks at sub-field granularity (attribute vs value, node vs role) so the
 * message is precise even when only one side of a pair is empty.
 */
export const buildAffiliationValidationMessage = (
  aff: Omit<TAffiliation, 'authConfigId'>
): string => {
  const missing: string[] = [];

  if (aff.matchingCriteria.some((c) => !c.attribute))
    missing.push('Attributes & values — attribute');
  if (aff.matchingCriteria.some((c) => !c.value))
    missing.push('Attributes & values — value');
  if (aff.organizationsAndRoles.some((o) => !o.organizationId))
    missing.push('Nodes & roles — node');
  if (aff.organizationsAndRoles.some((o) => !o.role))
    missing.push('Nodes & roles — role');

  return `Required fields missing: ${missing.join(', ')}`;
};
